import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { User, Link, History, Settings, RefreshCw, Shield, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

import { toast } from 'sonner';
import { SteamIcon } from './icons/SteamIcon';
import { reachGoal, GOAL_DEPOSIT_INIT, GOAL_DEPOSIT_SUCCESS } from '@/lib/metrika';
import { TradeUrlInput } from './TradeUrlInput';
import { SteamSecurityCheck } from './SteamSecurityCheck';
import { profileApi, balanceApi, paymentsApi, purchaseApi, type Profile, type Transaction } from '@/lib/api';
import { OrderStatusModal } from './OrderStatusModal';

interface ProfilePageProps {
  user: Profile | null;
  isAuthenticated: boolean;
  onLogin: () => void;
  onUpdateTradeUrl: (url: string) => void;
  onRefreshUser?: () => void;
}

export function ProfilePage({ user, isAuthenticated, onLogin, onUpdateTradeUrl, onRefreshUser }: ProfilePageProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tradeUrl, setTradeUrl] = useState(user?.steam_trade_url || '');
  const [showDepositDialog, setShowDepositDialog] = useState(false);
  // Активная вкладка — управляется через URL (?tab=history/settings/security)
  const [activeTab, setActiveTab] = useState(
    searchParams.get('tab') || 'settings'
  );
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [depositAmount, setDepositAmount] = useState(1000);
  const [customAmount, setCustomAmount] = useState('');

  const [depositLoading, setDepositLoading] = useState(false);
  const [orderStatusTxId, setOrderStatusTxId] = useState<number | null>(null);
  const [balance, setBalance] = useState<number | null>(null);
  // Ссылка на popup-окно оплаты — для закрытия при подтверждении
  const paymentPopupRef = useRef<Window | null>(null);

  // Обрабатываем URL-параметры при монтировании:
  // ?tab=history → переключаем вкладку
  // ?action=deposit → сразу открываем диалог пополнения
  useEffect(() => {
    const tab = searchParams.get('tab');
    const action = searchParams.get('action');
    if (tab) setActiveTab(tab);
    if (action === 'deposit') {
      setShowDepositDialog(true);
      // Убираем action из URL чтобы не открывалось повторно при refresh
      const next = new URLSearchParams(searchParams);
      next.delete('action');
      setSearchParams(next, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getStoredPayment = (txId: number) => {
    try { return JSON.parse(localStorage.getItem(`xpanda_payment_${txId}`) || 'null') as { url: string; formData: Record<string, string> } | null; }
    catch { return null; }
  };

  const loadBalance = async () => {
    try {
      const data = await balanceApi.getBalance() as { balance: number };
      setBalance(data.balance);
    } catch {
      setBalance(null);
    }
  };

  useEffect(() => {
    if (isAuthenticated && user) {
      loadTransactions();
      loadBalance();
    }
  }, [isAuthenticated, user]);

  // Check payment status on load (for Invizo callback return)
  useEffect(() => {
    const checkPaymentStatus = async () => {
      const params = new URLSearchParams(window.location.search);
      const orderNumber = params.get('number');
      const errorCode = params.get('errorcode');
      const errorText = params.get('errortext');
      
      if (orderNumber && isAuthenticated) {
        try {
          // Extract transaction_id from order number (format: purchase-{user_id}-{transaction_id}-{timestamp})
          const parts = orderNumber.split('-');
          const transactionId = parts.length >= 3 ? parseInt(parts[2]) : null;
          
          // Check payment status from backend
          const status = await paymentsApi.checkInvizoStatus(orderNumber);
          
          if (status.status === 'paid' && transactionId) {
            // Payment received, now confirm and create Xpanda deal
            toast.info('Подтверждаем покупку...');
            try {
              await purchaseApi.confirmPurchase(transactionId);
            } catch (confirmError: any) {
              console.error('Failed to confirm purchase:', confirmError);
              toast.error(`Ошибка подтверждения: ${confirmError.message || 'Неизвестная ошибка'}`);
            }
            setOrderStatusTxId(transactionId);
            loadTransactions();
            if (onRefreshUser) onRefreshUser();
          } else if (status.status === 'completed') {
            // Already completed (maybe refreshed page)
            setOrderStatusTxId(transactionId);
            loadTransactions();
            if (onRefreshUser) onRefreshUser();
          } else if (status.status === 'failed') {
            toast.error('Оплата не удалась. Попробуйте снова.');
          } else if (status.status === 'pending') {
            toast.info('Платёж обрабатывается. Обновите страницу через минуту.');
          }
          
          // Clean URL
          window.history.replaceState({}, '', '/profile');
        } catch (error) {
          console.error('Failed to check payment status:', error);
          // If we can't check status but there's an error code from Invizo
          if (errorCode) {
            toast.error(`Ошибка платежа: ${decodeURIComponent(errorText || 'Неизвестная ошибка')}`);
          }
        }
      }
    };
    
    checkPaymentStatus();
  }, [isAuthenticated]);

  useEffect(() => {
    if (user?.steam_trade_url) {
      setTradeUrl(user.steam_trade_url);
    }
  }, [user]);

  const loadTransactions = async () => {
    try {
      setLoadingTransactions(true);
      const data = await profileApi.getTransactions(50);
      setTransactions(data.transactions);
    } catch (error: any) {
      console.error('Failed to load transactions:', error);
    } finally {
      setLoadingTransactions(false);
    }
  };

const handleSaveTradeUrl = async () => {
    await onUpdateTradeUrl(tradeUrl);
  };

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/[^0-9]/g, ''); // только цифры
    setCustomAmount(val);
    setDepositAmount(val ? parseInt(val, 10) : 0);
  };

  const handleDeposit = async () => {
    if (depositAmount < 100) {
      toast.error('Минимальная сумма пополнения 100 ₽');
      return;
    }
    
    reachGoal(GOAL_DEPOSIT_INIT, { amount: depositAmount });
    setDepositLoading(true);
    try {
      const result = await balanceApi.createDeposit(depositAmount, 'invizo', '', '');
      
      // Если пришли параметры формы, создаем и отправляем форму на paymentlnk
      if (result.form_data && result.payment_url) {
        // Депозит создан успешно — фиксируем цель
        reachGoal(GOAL_DEPOSIT_SUCCESS, { amount: depositAmount });
        toast.success('Перенаправляем на оплату...');

        try {
          // Открываем popup ДО submit — синхронно в click-handler, браузер не блокирует.
          const popup = window.open(
            '',
            'xpanda-deposit',
            'width=560,height=700,scrollbars=yes,resizable=yes',
          );
          paymentPopupRef.current = popup;

          // Подсказка про VPN — страница Invizo не грузится при включённом VPN
          toast('🔒 Если страница оплаты не загружается — отключите VPN', { duration: 7000 });

          const form = document.createElement('form');
          form.method = 'POST';
          form.action = result.payment_url;
          form.target = popup ? 'xpanda-deposit' : '_blank';
          form.enctype = 'application/x-www-form-urlencoded';
          form.style.display = 'none';

          Object.entries(result.form_data).forEach(([key, value]) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = key;
            input.value = String(value);
            form.appendChild(input);
          });

          document.body.appendChild(form);
          form.submit();
          document.body.removeChild(form);

          // Закрываем диалог и открываем OrderStatusModal для отслеживания
          if (result.transaction_id) setOrderStatusTxId(result.transaction_id);
          setShowDepositDialog(false);

          if (!popup) {
            toast('Оплата открыта в новой вкладке — разрешите всплывающие окна для лучшего опыта', { duration: 5000 });
          }
        } catch (err) {
          console.error('[DEPOSIT] Error:', err);
          toast.error('Ошибка при создании формы оплаты');
        }
        return;
      }
      
      console.error('[DEPOSIT] No form_data:', result);
      toast.error('Ошибка создания платежа: нет данных формы');
    } catch (error: any) {
      toast.error(error.message || 'Ошибка создания платежа');
    } finally {
      setDepositLoading(false);
    }
  };

  // Not authenticated state
  if (!isAuthenticated) {
    return (
      <div className="w-full px-4 sm:px-6 lg:px-8 py-20">
        <div className="max-w-md mx-auto text-center">
          <div className="w-24 h-24 bg-[#1a2332] rounded-full flex items-center justify-center mx-auto mb-6">
            <User className="w-12 h-12 text-[#8b949e]" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">Требуется авторизация</h1>
          <p className="text-[#8b949e] mb-6">Войдите через Steam для доступа к профилю</p>
          <Button onClick={onLogin} className="bg-gradient-to-r from-[#1b2838] to-[#2a475e] text-white hover:from-[#2a475e] hover:to-[#1b2838]">
            <SteamIcon className="w-5 h-5 mr-2" />
            Войти через Steam
          </Button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="w-full px-4 sm:px-6 lg:px-8 py-20 text-center">
        <RefreshCw className="w-10 h-10 text-[#ff6b00] animate-spin mx-auto" />
      </div>
    );
  }

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Profile Header */}
        <div className="bg-gradient-to-r from-[#1a2332] to-[#161b22] rounded-2xl p-6 mb-6 border border-[#30363d]">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
            {/* Avatar */}
            <div className="relative">
              <div className="w-24 h-24 md:w-32 md:h-32 rounded-full bg-gradient-to-br from-[#ff6b00] to-[#ff9500] p-1">
                <div className="w-full h-full rounded-full bg-[#0d1117] flex items-center justify-center overflow-hidden">
                  {user.steam_avatar ? (
                    <img src={user.steam_avatar} alt="Avatar" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-12 h-12 md:w-16 md:h-16 text-[#8b949e]" />
                  )}
                </div>
              </div>
              <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-[#1b2838] rounded-full flex items-center justify-center border-2 border-[#161b22]">
                <SteamIcon className="w-4 h-4 text-[#66c0f4]" />
              </div>
            </div>

            {/* User Info */}
            <div className="flex-1 text-center md:text-left">
              <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
                {user.steam_name || 'Пользователь Steam'}
              </h1>
              <p className="text-[#8b949e] mb-4">Steam ID: {user.steam_id}</p>
              
              {/* Steam Status */}
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <Badge className="bg-[#1b2838] text-[#66c0f4] border-[#66c0f4]/30 px-3 py-1">
                  <SteamIcon className="w-4 h-4 mr-1" />
                  Steam подключен
                </Badge>
              </div>

              {/* Balance */}
              <div className="mt-4 flex flex-col sm:flex-row items-center gap-3">
                <div className="flex items-center gap-2 px-4 py-2 bg-[#0d1117] rounded-lg border border-[#30363d]">
                  <Wallet className="w-5 h-5 text-[#ff6b00]" />
                  <span className="text-[#8b949e]">Баланс:</span>
                  <span className="text-white font-bold text-lg">
                    {balance !== null ? `${balance.toLocaleString('ru-RU')} ₽` : '—'}
                  </span>
                </div>
                <Button
                  onClick={() => setShowDepositDialog(true)}
                  className="bg-gradient-to-r from-[#ff6b00] to-[#ff8c00] text-white hover:from-[#ff8c00] hover:to-[#ff6b00]"
                >
                  <Wallet className="w-4 h-4 mr-2" />
                  Пополнить
                </Button>
              </div>
            </div>


          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-[#161b22] border border-[#30363d] p-1 mb-6">
            <TabsTrigger 
              value="settings"
              className="data-[state=active]:bg-[#ff6b00] data-[state=active]:text-white text-[#8b949e]"
            >
              <Settings className="w-4 h-4 mr-2" />
              Настройки
            </TabsTrigger>
            <TabsTrigger 
              value="security"
              className="data-[state=active]:bg-[#ff6b00] data-[state=active]:text-white text-[#8b949e]"
            >
              <Shield className="w-4 h-4 mr-2" />
              Безопасность
            </TabsTrigger>
            <TabsTrigger 
              value="history"
              className="data-[state=active]:bg-[#ff6b00] data-[state=active]:text-white text-[#8b949e]"
            >
              <History className="w-4 h-4 mr-2" />
              История
            </TabsTrigger>
          </TabsList>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card className="bg-[#161b22] border-[#30363d]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <SteamIcon className="w-6 h-6 text-[#66c0f4]" />
                  Настройки Steam
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Trade URL */}
                <div className="p-4 bg-[#0d1117] rounded-lg border border-[#30363d]">
                  <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                    <Link className="w-5 h-5 text-[#ff6b00]" />
                    Trade URL
                  </h3>
                  <TradeUrlInput 
                    value={tradeUrl}
                    onChange={setTradeUrl}
                    onSave={handleSaveTradeUrl}
                  />
                </div>

              </CardContent>
            </Card>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security">
            <div className="space-y-6">
              {/* Steam Security Check */}
              <SteamSecurityCheck steamId={user.steam_id} />

              {/* Account Info */}
              <Card className="bg-[#161b22] border-[#30363d]">
                <CardHeader>
                  <CardTitle className="text-white">Информация об аккаунте</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between py-2 border-b border-[#30363d]">
                    <span className="text-[#8b949e]">Steam ID64</span>
                    <span className="text-white font-mono">{user.steam_id}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-[#30363d]">
                    <span className="text-[#8b949e]">Имя пользователя</span>
                    <span className="text-white">{user.steam_name}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-[#8b949e]">Дата регистрации</span>
                    <span className="text-white">
                      {new Date(user.created_at).toLocaleDateString('ru-RU')}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <Card className="bg-[#161b22] border-[#30363d]">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white">История транзакций</CardTitle>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => setShowDepositDialog(true)}
                    className="bg-gradient-to-r from-[#ff6b00] to-[#ff8c00] text-white hover:from-[#ff8c00] hover:to-[#ff6b00]"
                  >
                    Пополнить баланс
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={loadTransactions}
                    disabled={loadingTransactions}
                  >
                    <RefreshCw className={`w-4 h-4 ${loadingTransactions ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {transactions.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-[#8b949e]">Нет транзакций</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {transactions.map((tx) => {
                      const chip = kindChip(tx.display_kind || tx.type);
                      const sign = tx.amount_sign ?? (tx.amount > 0 ? '+' : '');
                      const amountColor = sign === '+' ? 'text-green-400' : sign === '-' ? 'text-white' : 'text-[#8b949e]';
                      return (
                      <div
                        key={tx.id}
                        className="flex items-center justify-between p-4 bg-[#0d1117] rounded-lg border border-[#30363d]"
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`text-xs font-medium ${chip.cls}`}>{chip.label}</span>
                          </div>
                          <p className="text-white font-medium truncate">
                            {tx.item_name || getTransactionTypeName(tx.type)}
                          </p>
                          <p className="text-[#8b949e] text-sm">{new Date(tx.created_at).toLocaleString('ru-RU')}</p>
                        </div>
                        <div className="text-right shrink-0 ml-3">
                          <p className={`font-bold ${amountColor}`}>
                            {sign}{tx.amount.toFixed(0)} {tx.currency}
                          </p>
                          <Badge className={`${displayStatusStyle(tx.display_status)} mt-1`}>
                            {tx.display_label || getStatusName(tx.status)}
                          </Badge>
                          {(tx.display_kind === 'purchase' || tx.type === 'purchase') && (
                            <button
                              onClick={() => setOrderStatusTxId(tx.id)}
                              className="text-[#ff6b00] text-xs hover:underline mt-1 block ml-auto"
                            >
                              Статус заказа
                            </button>
                          )}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Deposit Dialog */}
        <Dialog open={showDepositDialog} onOpenChange={(open) => {
          setShowDepositDialog(open);
          if (!open) setCustomAmount('');
        }}>
          <DialogContent className="bg-[#161b22] border-[#30363d] text-white max-w-md">
            <DialogHeader>
              <DialogTitle>Пополнение баланса</DialogTitle>
              <DialogDescription className="text-[#8b949e]">
                Выберите сумму и способ оплаты
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Amount Selection */}
              <div>
                <label className="text-[#8b949e] text-sm mb-2 block">Сумма пополнения</label>
                <div className="grid grid-cols-3 gap-2">
                  {[500, 1000, 2000, 5000, 10000].map((amount) => (
                    <Button
                      key={amount}
                      variant={depositAmount === amount && customAmount === '' ? 'default' : 'outline'}
                      onClick={() => { setDepositAmount(amount); setCustomAmount(''); }}
                      className={depositAmount === amount && customAmount === ''
                        ? 'bg-[#ff6b00] text-white'
                        : 'border-[#30363d] text-[#8b949e]'
                      }
                    >
                      {amount} ₽
                    </Button>
                  ))}
                </div>
                {/* Произвольная сумма */}
                <div className="mt-3">
                  <label className="text-[#8b949e] text-xs mb-1.5 block">или введите свою сумму</label>
                  <div className="relative">
                    <Input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="от 100 ₽"
                      value={customAmount}
                      onChange={handleCustomAmountChange}
                      className="bg-[#0d1117] border-[#30363d] text-white placeholder:text-[#6e7681] pr-8 focus-visible:ring-[#ff6b00]"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8b949e] text-sm pointer-events-none">₽</span>
                  </div>
                </div>
              </div>

              
              {/* Summary */}
              <div className="bg-[#0d1117] rounded-lg p-4">
                <div className="flex justify-between mb-2">
                  <span className="text-[#8b949e]">Сумма:</span>
                  <span className="text-white font-bold">{depositAmount.toLocaleString('ru-RU')} ₽</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#8b949e]">Способ:</span>
                  <span className="text-white">СБП (Инвизо)</span>
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowDepositDialog(false)}
                  className="flex-1 border-[#30363d] text-[#8b949e]"
                  disabled={depositLoading}
                >
                  Отмена
                </Button>
                <Button
                  onClick={handleDeposit}
                  disabled={depositLoading}
                  className="flex-1 bg-[#ff6b00] hover:bg-[#ff8533] text-white h-12"
                >
                  {depositLoading ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Wallet className="w-5 h-5 mr-2" />
                      Пополнить
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Order Status Modal */}
        <OrderStatusModal
          transactionId={orderStatusTxId}
          onClose={() => setOrderStatusTxId(null)}
          onPaymentConfirmed={() => {
            paymentPopupRef.current?.close();
            paymentPopupRef.current = null;
            loadBalance();
          }}
          paymentUrl={orderStatusTxId ? getStoredPayment(orderStatusTxId)?.url : undefined}
          paymentFormData={orderStatusTxId ? getStoredPayment(orderStatusTxId)?.formData : undefined}
        />
      </div>
    </div>
  );
}

function getTransactionTypeName(type: string): string {
  const names: Record<string, string> = {
    'deposit': 'Пополнение баланса',
    'purchase': 'Покупка',
    'sale': 'Продажа',
    'refund': 'Возврат',
    'gift': 'Подарок',
  };
  return names[type] || type;
}

function getStatusName(status: string): string {
  const names: Record<string, string> = {
    'pending': 'В обработке',
    'completed': 'Завершено',
    'cancelled': 'Отменено',
    'failed': 'Ошибка',
  };
  return names[status] || status;
}

// Цвет бейджа по canonical display_status (бэк _compute_display).
function displayStatusStyle(ds?: string): string {
  const ok = 'bg-green-500/20 text-green-400 border-green-500/30';
  const inprog = 'bg-blue-500/20 text-blue-400 border-blue-500/30';
  const wait = 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
  const back = 'bg-teal-500/20 text-teal-400 border-teal-500/30';
  const fail = 'bg-red-500/20 text-red-400 border-red-500/30';
  const grey = 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  const map: Record<string, string> = {
    deposit_completed: ok,
    deposit_pending: wait,
    deposit_failed: grey,
    purchase_delivered: ok,
    purchase_buying: inprog,
    purchase_sending: inprog,
    purchase_preparing: wait,
    purchase_pending_payment: wait,
    purchase_refunded: back,
    refund_to_balance: back,
    purchase_failed: fail,
  };
  return map[ds || ''] || grey;
}

// Чип типа операции.
function kindChip(kind?: string): { label: string; cls: string } {
  switch (kind) {
    case 'deposit': return { label: '⬇ Пополнение', cls: 'text-green-400' };
    case 'purchase': return { label: '🛒 Покупка', cls: 'text-white' };
    case 'refund': return { label: '↩ Возврат', cls: 'text-teal-400' };
    default: return { label: kind || '', cls: 'text-[#8b949e]' };
  }
}
